import { resolveConnectionCapacity, type ConnectionLimits } from '@common/lib/connection-limits';
import { PLUGIN_PLAN_LIMITS } from "@/utils/pluginWorkspace";
import {
  getConnectableStorefrontIntegrationTools,
  getIntegrationProviderForTool,
  normalizeIntegrationProvider,
} from "../Settings/components/integrations/integrationCatalog";
import {
  normalizeShopifyStoreDomainInput,
  resolveShopifyConnectionDomain,
} from "./storeUrlNormalization";
import type { StoreTrialEligibilityState } from "@common/api/authService";

export type OnboardingConnectionInstanceStatus =
  | "connected"
  | "pending"
  | "disconnected"
  | "error";

export interface OnboardingConnectionMappingLike {
  id?: number | string | null;
  slugId?: string | null;
  label?: string | null;
  status?: string | boolean | number | null;
  mapping_status?: string | boolean | number | null;
  account_status?: string | boolean | number | null;
  connection_id?: string | null;
  nango_connection_id?: string | null;
  account_id?: string | null;
  customer_id?: string | null;
  account_name?: string | null;
  name?: string | null;
  store_name?: string | null;
  shop_name?: string | null;
  shop_domain?: string | null;
  shopDomain?: string | null;
  store_url?: string | null;
  storeUrl?: string | null;
  store_hash?: string | null;
  storeHash?: string | null;
  store_domain?: string | null;
  storeDomain?: string | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
  authorized_account_id?: string | null;
  authorizedAccountId?: string | null;
  external_account_name?: string | null;
  externalAccountName?: string | null;
  authorized_account_name?: string | null;
  authorizedAccountName?: string | null;
  external_account_email?: string | null;
  externalAccountEmail?: string | null;
  authorized_account_email?: string | null;
  authorizedAccountEmail?: string | null;
  authType?: string | null;
  native?: boolean | null;
  restrictionState?: StoreTrialEligibilityState | string | null;
  eligibilityState?: StoreTrialEligibilityState | string | null;
  allowsFreshTrial?: boolean | null;
  eligibilityMessage?: string | null;
  message?: string | null;
  provider?: string | null;
  /** Provider-owned resources under one authorization; not separate connections. */
  sub_accounts?: readonly unknown[] | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OnboardingServerIntegrationLike {
  id?: number | string | null;
  provider?: string | null;
  connected?: boolean | null;
  status?: string | boolean | number | null;
  mapping_status?: string | boolean | number | null;
  account_status?: string | boolean | number | null;
  mappings?: readonly OnboardingConnectionMappingLike[] | null;
  profile_data?: Record<string, unknown> | null;
  external_account_id?: string | null;
  externalAccountId?: string | null;
  external_account_name?: string | null;
  externalAccountName?: string | null;
  authType?: string | null;
  native?: boolean | null;
  restrictionState?: StoreTrialEligibilityState | string | null;
  eligibilityState?: StoreTrialEligibilityState | string | null;
  allowsFreshTrial?: boolean | null;
  eligibilityMessage?: string | null;
  message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OnboardingConnectionInstance {
  /** Stable UI/operation key. This is intentionally separate from dedupeKey. */
  id: string;
  provider: string;
  dedupeKey: string;
  externalIdentity: string;
  accountName: string;
  label: string;
  status: OnboardingConnectionInstanceStatus;
  isValidConnection: boolean;
  connectionId?: string;
  workspaceIntegrationId?: string;
  shopDomain?: string;
  slugId?: string;
  authType?: string;
  native?: boolean;
  restrictionState?: StoreTrialEligibilityState | null;
  eligibilityState?: StoreTrialEligibilityState | null;
  allowsFreshTrial?: boolean | null;
  eligibilityMessage?: string | null;
  eligibilityMessageFallback?: string | null;
  sourceIntegrationIndex: number;
  sourceMappingIndex: number | null;
}

export interface OnboardingConnectionDisconnectOptions {
  connectionId?: string;
  workspaceIntegrationId?: string;
  shopDomain?: string;
  authType?: string;
  native?: boolean;
  connectionScene: "onboarding";
}

export interface OnboardingConnectionProviderGroup {
  provider: string;
  instances: OnboardingConnectionInstance[];
  validInstances: OnboardingConnectionInstance[];
  totalCount: number;
  validCount: number;
}

export type PluginConnectionKind = "store" | "other";

export interface PluginConnectionUsage {
  storeConnections: number;
  otherIntegrationConnections: number;
  totalIntegrationConnections: number;
  remainingStoreConnections: number;
  remainingOtherIntegrationConnections: number;
  remainingTotalIntegrationConnections: number;
}

export type PluginConnectionLimitReason =
  | "store_limit_reached"
  | "integration_group_limit_reached"
  | "total_connection_limit_reached";

export interface PluginConnectionAdditionState {
  allowed: boolean;
  connectionKind: PluginConnectionKind;
  reason: PluginConnectionLimitReason | null;
  current: number;
  limit: number;
  usage: PluginConnectionUsage;
}

export const BUSTLY_CORE_ONBOARDING_LIMITS = {
  storeConnections: 1,
  totalIntegrationConnections: 5,
} as const;

const CONNECTED_STATUS_VALUES = new Set([
  "1",
  "true",
  "active",
  "connected",
  "success",
  "succeeded",
]);

const PENDING_STATUS_VALUES = new Set([
  "authorizing",
  "connecting",
  "incomplete",
  "pending",
  "processing",
  "syncing",
]);

const ERROR_STATUS_VALUES = new Set(["error", "failed", "invalid"]);

const DISCONNECTED_STATUS_VALUES = new Set([
  "0",
  "false",
  "deactivated",
  "disabled",
  "disconnected",
  "expired",
  "inactive",
  "revoked",
  "suspended",
  "trial-expired",
  "trial_expired",
  "unauthorized",
]);

const FREE_TRIAL_USED_RESTRICTION_STATES = new Set<StoreTrialEligibilityState>([
  "already_bound",
  "trial_valid",
  "trial_expired",
]);

const isTrialResumeAllowed = (state?: StoreTrialEligibilityState | null): boolean => (
  state === "trial_resume_allowed"
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const normalizeText = (value: unknown): string =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? String(value).trim()
    : "";

const firstText = (...values: unknown[]): string => {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) return normalized;
  }

  return "";
};

const firstOptionalText = (...values: unknown[]): string | null => {
  const text = firstText(...values);
  return text || null;
};

const firstDefined = <T>(...values: Array<T | null | undefined>): T | null => {
  for (const value of values) {
    if (value !== null && value !== undefined) return value;
  }

  return null;
};

const normalizeStatusValue = (value: unknown): string =>
  firstText(value).toLowerCase().replace(/\s+/g, "_");

const normalizeTrialEligibilityState = (
  value: unknown,
): StoreTrialEligibilityState | null => {
  const normalized = normalizeStatusValue(value);
  if (!normalized) return null;
  if (
    normalized === "eligible" ||
    normalized === "already_bound" ||
    normalized === "trial_valid" ||
    normalized === "trial_expired" ||
    normalized === "store_limit_reached" ||
    normalized === "integration_group_limit_reached" ||
    normalized === "total_connection_limit_reached"
  ) {
    return normalized;
  }

  return null;
};

const resolveInstanceStatus = (
  integration: OnboardingServerIntegrationLike,
  mapping?: OnboardingConnectionMappingLike,
): OnboardingConnectionInstanceStatus => {
  if (integration.connected !== true) {
    return "disconnected";
  }

  const statusValues = [
    mapping?.status,
    mapping?.mapping_status,
    mapping?.account_status,
    integration.status,
    integration.mapping_status,
    integration.account_status,
  ]
    .map(normalizeStatusValue)
    .filter(Boolean);

  if (statusValues.some((status) => ERROR_STATUS_VALUES.has(status))) {
    return "error";
  }

  if (statusValues.some((status) => DISCONNECTED_STATUS_VALUES.has(status))) {
    return "disconnected";
  }

  if (statusValues.some((status) => PENDING_STATUS_VALUES.has(status))) {
    return "pending";
  }

  if (
    statusValues.length === 0 ||
    statusValues.some((status) => CONNECTED_STATUS_VALUES.has(status))
  ) {
    return "connected";
  }

  // The workspace-level connection flag remains the fallback for providers
  // whose mapping status uses an account-specific vocabulary such as ENABLED.
  return "connected";
};

const normalizeIdentity = (provider: string, value: unknown): string => {
  const source = firstText(value);
  if (!source) return "";

  return source
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^\/+/, "")
    .replace(/[?#].*$/, "")
    .replace(/[/?#]+$/, "");
};

const getProfileIdentity = (
  provider: string,
  integration: OnboardingServerIntegrationLike,
): string => {
  const profile = asRecord(integration.profile_data);
  return normalizeIdentity(
    provider,
    firstText(
      integration.external_account_id,
      integration.externalAccountId,
      profile.external_account_id,
      profile.externalAccountId,
      profile.authorized_account_id,
      profile.authorizedAccountId,
      profile.account_id,
      profile.accountId,
      profile.customer_id,
      profile.customerId,
      profile.shop_domain,
      profile.shopDomain,
      profile.store_url,
      profile.storeUrl,
      integration.external_account_name,
      integration.externalAccountName,
      profile.external_account_name,
      profile.externalAccountName,
      profile.account_name,
      profile.accountName,
      profile.username,
    ),
  );
};

const getMappingIdentity = (
  provider: string,
  integration: OnboardingServerIntegrationLike,
  mapping: OnboardingConnectionMappingLike,
): string => {
  if (provider === "shopify") {
    const shopDomain = resolveShopifyConnectionDomain({
      integration,
      mapping,
    });
    if (shopDomain) return shopDomain;

    return normalizeIdentity(
      provider,
      firstText(
        mapping.connection_id,
        mapping.nango_connection_id,
        mapping.id,
        mapping.external_account_id,
        mapping.externalAccountId,
        mapping.account_id,
        mapping.account_name,
        mapping.name,
      ),
    );
  }

  if (provider === "bigcommerce") {
    return normalizeIdentity(
      provider,
      firstText(
        mapping.store_hash,
        mapping.storeHash,
        mapping.external_account_id,
        mapping.externalAccountId,
        mapping.connection_id,
        mapping.nango_connection_id,
      ),
    );
  }

  if (
    provider === "woocommerce" ||
    provider === "magento" ||
    provider === "prestashop"
  ) {
    return normalizeIdentity(
      provider,
      firstText(
        mapping.store_url,
        mapping.storeUrl,
        mapping.store_domain,
        mapping.storeDomain,
        mapping.shop_domain,
        mapping.shopDomain,
        mapping.external_account_id,
        mapping.externalAccountId,
        mapping.connection_id,
        mapping.nango_connection_id,
      ),
    );
  }

  return normalizeIdentity(
    provider,
    firstText(
      // Prefer account- or mapping-scoped identifiers before any value used
      // only for display. Two authorizations can legitimately share the same
      // account name, so names must never collapse distinct mappings when a
      // stable identifier is available.
      mapping.external_account_id,
      mapping.externalAccountId,
      mapping.authorized_account_id,
      mapping.authorizedAccountId,
      mapping.account_id,
      mapping.customer_id,
      mapping.id,
      mapping.connection_id,
      mapping.nango_connection_id,
      mapping.shop_domain,
      mapping.shopDomain,
      mapping.store_url,
      mapping.storeUrl,
      mapping.external_account_email,
      mapping.externalAccountEmail,
      mapping.authorized_account_email,
      mapping.authorizedAccountEmail,
      mapping.external_account_name,
      mapping.externalAccountName,
      mapping.authorized_account_name,
      mapping.authorizedAccountName,
      mapping.store_name,
      mapping.shop_name,
      mapping.account_name,
      mapping.name,
    ),
  );
};

const getInstanceExternalIdentity = ({
  integration,
  mapping,
  mappingCount,
  provider,
  integrationIndex,
  mappingIndex,
}: {
  integration: OnboardingServerIntegrationLike;
  mapping?: OnboardingConnectionMappingLike;
  mappingCount: number;
  provider: string;
  integrationIndex: number;
  mappingIndex: number | null;
}): string => {
  const mappingIdentity = mapping
    ? getMappingIdentity(provider, integration, mapping)
    : "";
  if (mappingIdentity) return mappingIdentity;

  // A profile belongs to the whole provider record. It is safe to use as the
  // account identity when that record represents one connection. For a record
  // with multiple mappings, prefer each mapping's own connection id so distinct
  // accounts cannot be collapsed by shared profile metadata.
  const profileIdentity = getProfileIdentity(provider, integration);
  if (mappingCount <= 1 && profileIdentity) return profileIdentity;

  const connectionIdentity = normalizeIdentity(
    provider,
    firstText(mapping?.connection_id, mapping?.nango_connection_id),
  );
  if (connectionIdentity) return connectionIdentity;

  if (profileIdentity) return profileIdentity;

  return normalizeIdentity(
    provider,
    firstText(
      mapping?.id,
      integration.id,
      mapping?.created_at,
      integration.created_at,
      mapping?.updated_at,
      integration.updated_at,
      `${integrationIndex}:${mappingIndex ?? "root"}`,
    ),
  );
};

const getInstanceAccountName = (
  provider: string,
  integration: OnboardingServerIntegrationLike,
  mapping?: OnboardingConnectionMappingLike,
): string => {
  const profile = asRecord(integration.profile_data);
  if (provider === "shopify") {
    const storeDomain = resolveShopifyConnectionDomain({
      integration,
      mapping,
    });
    if (storeDomain) return storeDomain;
  }

  const displayValue = firstText(
    mapping?.store_name,
    mapping?.shop_name,
    mapping?.shop_domain,
    mapping?.shopDomain,
    mapping?.store_url,
    mapping?.storeUrl,
    mapping?.authorized_account_name,
    mapping?.authorizedAccountName,
    mapping?.external_account_name,
    mapping?.externalAccountName,
    mapping?.account_name,
    mapping?.name,
    profile.shop_domain,
    profile.shopDomain,
    profile.store_url,
    profile.storeUrl,
    mapping?.account_id,
    mapping?.customer_id,
    profile.account_name,
    profile.accountName,
    profile.connection_name,
    profile.username,
    integration.external_account_name,
    integration.externalAccountName,
    mapping?.connection_id,
    mapping?.nango_connection_id,
  );

  return displayValue;
};

const buildConnectionInstance = ({
  integration,
  mapping,
  mappingCount,
  integrationIndex,
  mappingIndex,
}: {
  integration: OnboardingServerIntegrationLike;
  mapping?: OnboardingConnectionMappingLike;
  mappingCount: number;
  integrationIndex: number;
  mappingIndex: number | null;
}): OnboardingConnectionInstance | null => {
  const provider = normalizeIntegrationProvider(
    mapping?.provider || integration.provider,
  );
  if (!provider) return null;

  const externalIdentity = getInstanceExternalIdentity({
    integration,
    mapping,
    mappingCount,
    provider,
    integrationIndex,
    mappingIndex,
  });
  const status = resolveInstanceStatus(integration, mapping);
  const workspaceIntegrationId =
    firstText(mapping?.id, integration.id) || undefined;
  const connectionId =
    firstText(mapping?.connection_id, mapping?.nango_connection_id) ||
    undefined;
  const shopDomain =
    provider === "shopify"
      ? resolveShopifyConnectionDomain({ integration, mapping })
      : undefined;
  const operationIdentity =
    workspaceIntegrationId ||
    connectionId ||
    externalIdentity ||
    `${integrationIndex}:${mappingIndex ?? "root"}`;
  const integrationRestrictionState = normalizeTrialEligibilityState(
    firstDefined(integration.restrictionState, integration.eligibilityState),
  );
  const integrationEligibilityState = normalizeTrialEligibilityState(
    firstDefined(integration.eligibilityState, integration.restrictionState),
  );
  const integrationTrialUnavailable =
    !isTrialResumeAllowed(integrationRestrictionState || integrationEligibilityState) &&
    (integration.allowsFreshTrial === false ||
      FREE_TRIAL_USED_RESTRICTION_STATES.has(
        integrationRestrictionState || integrationEligibilityState || "eligible",
      ));
  const restrictionStateSource = mapping
    ? firstDefined(
        integrationTrialUnavailable ? integrationRestrictionState : null,
        integrationTrialUnavailable ? integrationEligibilityState : null,
        mapping.restrictionState,
        mapping.eligibilityState,
      )
    : firstDefined(integration.restrictionState, integration.eligibilityState);
  const eligibilityStateSource = mapping
    ? firstDefined(
        integrationTrialUnavailable ? integrationEligibilityState : null,
        integrationTrialUnavailable ? integrationRestrictionState : null,
        mapping.eligibilityState,
        mapping.restrictionState,
      )
    : firstDefined(integration.eligibilityState, integration.restrictionState);
  const restrictionState = normalizeTrialEligibilityState(
    restrictionStateSource,
  );
  const eligibilityState = normalizeTrialEligibilityState(
    eligibilityStateSource,
  );

  return {
    id: `${provider}:${operationIdentity}`,
    provider,
    dedupeKey: `${provider}:${externalIdentity || operationIdentity}`,
    externalIdentity: externalIdentity || operationIdentity,
    accountName:
      getInstanceAccountName(provider, integration, mapping) ||
      externalIdentity ||
      provider,
    label: firstText(mapping?.label),
    status,
    isValidConnection: status === "connected",
    connectionId,
    workspaceIntegrationId,
    shopDomain,
    slugId: firstText(mapping?.slugId) || undefined,
    authType: firstText(mapping?.authType, integration.authType) || undefined,
    native:
      typeof mapping?.native === "boolean"
        ? mapping.native
        : typeof integration.native === "boolean"
          ? integration.native
          : undefined,
    restrictionState,
    eligibilityState,
    allowsFreshTrial:
      integrationTrialUnavailable &&
      typeof integration.allowsFreshTrial === "boolean"
        ? integration.allowsFreshTrial
        : typeof mapping?.allowsFreshTrial === "boolean"
          ? mapping.allowsFreshTrial
          : typeof integration.allowsFreshTrial === "boolean"
            ? integration.allowsFreshTrial
            : null,
    eligibilityMessage: firstOptionalText(
      mapping?.eligibilityMessage,
      ...(mapping && !integrationTrialUnavailable
        ? []
        : [integration.eligibilityMessage]),
    ),
    eligibilityMessageFallback: firstOptionalText(
      mapping?.message,
      ...(mapping && !integrationTrialUnavailable ? [] : [integration.message]),
    ),
    sourceIntegrationIndex: integrationIndex,
    sourceMappingIndex: mappingIndex,
  };
};

/**
 * Builds the instance-scoped identity required by each disconnect transport.
 * Keeping this pure prevents the onboarding UI from falling back to a
 * provider-wide disconnect when the same app has multiple accounts.
 */
export const buildOnboardingConnectionDisconnectOptions = (
  instance: OnboardingConnectionInstance,
): OnboardingConnectionDisconnectOptions => ({
  connectionId: instance.connectionId,
  workspaceIntegrationId: instance.workspaceIntegrationId,
  shopDomain: instance.provider === "shopify" ? instance.shopDomain : undefined,
  authType: instance.authType,
  native: instance.native,
  connectionScene: "onboarding",
});

export const hasDuplicateOnboardingShopifyConnection = (
  instances: readonly OnboardingConnectionInstance[],
  shopDomain: string,
): boolean => {
  const normalizedShopDomain = normalizeShopifyStoreDomainInput(shopDomain);
  if (!normalizedShopDomain) {
    return false;
  }

  return instances.some(
    (instance) =>
      instance.provider === "shopify" &&
      instance.isValidConnection &&
      instance.shopDomain === normalizedShopDomain,
  );
};

export const hasDuplicateOnboardingStoreConnection = (
  instances: readonly OnboardingConnectionInstance[],
  provider: string,
  externalIdentity: string,
): boolean => {
  const normalizedProvider = normalizeIntegrationProvider(provider);
  const normalizedExternalIdentity = normalizeIdentity(
    normalizedProvider,
    externalIdentity,
  );
  if (!normalizedProvider || !normalizedExternalIdentity) {
    return false;
  }

  return instances.some(
    (instance) =>
      instance.isValidConnection &&
      instance.provider === normalizedProvider &&
      normalizeIdentity(instance.provider, instance.externalIdentity) ===
        normalizedExternalIdentity,
  );
};

export const resolveOnboardingStoreConnectionIdentity = (
  provider: string,
  connectionInput: unknown,
): string => {
  const normalizedProvider = normalizeIntegrationProvider(provider);
  const input = asRecord(connectionInput);
  const identity =
    normalizedProvider === "bigcommerce"
      ? firstText(
          input.store_hash,
          input.storeHash,
          input.external_account_id,
          input.externalAccountId,
        )
      : normalizedProvider === "shopify"
        ? firstText(
            input.shop_domain,
            input.shopDomain,
            input.storeUrl,
            input.storeURL,
          )
        : firstText(
            input.storeUrl,
            input.storeURL,
            input.storeDomain,
            input.store_domain,
            input.domain,
            input.external_account_id,
            input.externalAccountId,
          );

  return normalizeIdentity(
    normalizedProvider,
    typeof connectionInput === "string" ? connectionInput : identity,
  );
};

export const resolveOnboardingConnectionInstances = (
  integrations: readonly OnboardingServerIntegrationLike[] = [],
): OnboardingConnectionInstance[] => {
  const flattened = integrations.flatMap((integration, integrationIndex) => {
    const mappings = integration.mappings || [];

    if (mappings.length > 0) {
      return mappings
        .map((mapping, mappingIndex) =>
          buildConnectionInstance({
            integration,
            mapping,
            mappingCount: mappings.length,
            integrationIndex,
            mappingIndex,
          }),
        )
        .filter(
          (instance): instance is OnboardingConnectionInstance =>
            instance !== null,
        );
    }

    const instance = buildConnectionInstance({
      integration,
      mappingCount: 0,
      integrationIndex,
      mappingIndex: null,
    });
    return instance ? [instance] : [];
  });

  const dedupedByExternalIdentity = new Map<
    string,
    OnboardingConnectionInstance
  >();
  flattened.forEach((instance) => {
    const current = dedupedByExternalIdentity.get(instance.dedupeKey);

    // Prefer a usable record when stale failed/disconnected duplicates of the
    // same external account are also present in the response.
    if (
      !current ||
      (!current.isValidConnection && instance.isValidConnection)
    ) {
      dedupedByExternalIdentity.set(instance.dedupeKey, instance);
    }
  });

  return Array.from(dedupedByExternalIdentity.values());
};

export const hasOnboardingConnectionUsedTrial = (
  instance: Pick<
    OnboardingConnectionInstance,
    "allowsFreshTrial" | "eligibilityState" | "restrictionState"
  >,
): boolean => {
  const state = instance.restrictionState ?? instance.eligibilityState ?? null;
  return Boolean(
    state &&
      FREE_TRIAL_USED_RESTRICTION_STATES.has(state) &&
      instance.allowsFreshTrial === false,
  );
};

export const groupOnboardingConnectionInstancesByProvider = (
  instances: readonly OnboardingConnectionInstance[],
): OnboardingConnectionProviderGroup[] => {
  const groups = new Map<string, OnboardingConnectionInstance[]>();

  instances.forEach((instance) => {
    const existing = groups.get(instance.provider) || [];
    existing.push(instance);
    groups.set(instance.provider, existing);
  });

  return Array.from(groups.entries()).map(([provider, providerInstances]) => {
    const validInstances = providerInstances.filter(
      (instance) => instance.isValidConnection,
    );

    return {
      provider,
      instances: providerInstances,
      validInstances,
      totalCount: providerInstances.length,
      validCount: validInstances.length,
    };
  });
};

const STORE_PROVIDER_SET = new Set(
  getConnectableStorefrontIntegrationTools()
    .map((tool) => getIntegrationProviderForTool(tool))
    .map((provider) => normalizeIntegrationProvider(provider))
    .filter(Boolean),
);

export const getOnboardingConnectionKind = (
  provider?: string | null,
): PluginConnectionKind =>
  STORE_PROVIDER_SET.has(normalizeIntegrationProvider(provider))
    ? "store"
    : "other";

const resolveConnectionUsage = (
  instances: readonly OnboardingConnectionInstance[],
  limits: ConnectionLimits,
): PluginConnectionUsage => {
  const validInstances = instances.filter(
    (instance) => instance.isValidConnection,
  );
  const storeConnections = validInstances.filter(
    (instance) => getOnboardingConnectionKind(instance.provider) === "store",
  ).length;
  const totalIntegrationConnections = validInstances.length;
  const otherIntegrationConnections =
    totalIntegrationConnections - storeConnections;

  return {
    storeConnections,
    otherIntegrationConnections,
    totalIntegrationConnections,
    remainingStoreConnections: Math.max(
      0,
      limits.storeConnections - storeConnections,
    ),
    remainingOtherIntegrationConnections: Math.max(
      0,
      limits.otherIntegrationConnections === undefined
        ? limits.totalIntegrationConnections - totalIntegrationConnections
        : limits.otherIntegrationConnections - otherIntegrationConnections,
    ),
    remainingTotalIntegrationConnections: Math.max(
      0,
      limits.totalIntegrationConnections -
        totalIntegrationConnections,
    ),
  };
};

export const resolvePluginConnectionUsage = (
  instances: readonly OnboardingConnectionInstance[],
): PluginConnectionUsage => resolveConnectionUsage(instances, PLUGIN_PLAN_LIMITS);

export const resolveOnboardingConnectionUsage = (
  instances: readonly OnboardingConnectionInstance[],
  isPluginOnboarding: boolean,
): PluginConnectionUsage =>
  resolveConnectionUsage(
    instances,
    isPluginOnboarding ? PLUGIN_PLAN_LIMITS : BUSTLY_CORE_ONBOARDING_LIMITS,
  );

const resolveConnectionAdditionState = ({
  instances,
  provider,
  limits,
}: {
  instances: readonly OnboardingConnectionInstance[];
  provider?: string | null;
  limits: ConnectionLimits;
}): PluginConnectionAdditionState => {
  const usage = resolveConnectionUsage(instances, limits);
  const connectionKind = getOnboardingConnectionKind(provider);

  return {
    ...resolveConnectionCapacity(usage, limits, connectionKind),
    connectionKind,
    usage,
  };
};

export const resolvePluginConnectionAdditionState = ({
  instances,
  provider,
}: {
  instances: readonly OnboardingConnectionInstance[];
  provider?: string | null;
}): PluginConnectionAdditionState =>
  resolveConnectionAdditionState({
    instances,
    provider,
    limits: PLUGIN_PLAN_LIMITS,
  });

/**
 * Core and Plugin onboarding share the same instance accordion while applying
 * their product-specific connection limits.
 */
export const resolveOnboardingConnectionAdditionState = ({
  instances,
  provider,
  isPluginOnboarding,
}: {
  instances: readonly OnboardingConnectionInstance[];
  provider?: string | null;
  isPluginOnboarding: boolean;
}): PluginConnectionAdditionState =>
  resolveConnectionAdditionState({
    instances,
    provider,
    limits: isPluginOnboarding
      ? PLUGIN_PLAN_LIMITS
      : BUSTLY_CORE_ONBOARDING_LIMITS,
  });
